import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import LocationCell from '@components/DataGrid/LocationCell';
import { EditableCellRef, EditSaveScope, defaultSortComparator } from '@components/DataGrid/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useState } from 'react';
import { EditableCell, resolveEditTargetRows } from '../types';
import DisabledTooltip from '@components/DisabledTooltip';
import { LegLocation } from '@api/Trinium/Endpoints/Leg';
import { usePatchLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import ButtonDropdown from '@forms/Components/Dropdowns/ButtonDropdown';
import LocationButtonContent from '@forms/Appointment/LocationButtonContent';
import LocationListContent from '@forms/Appointment/LocationListContent';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { LegStatus } from '@api/Trinium/types';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

const pickupField = 'Pickup';
const headerDisplayName = 'Pickup';

/** Sub-values of the Pickup column sortable via hidden companion columns */
const pickupSubColumns: SubColumnDef<DispatchLeg>[] = [
  { key: 'city', label: 'City', valueGetter: (row) => row.puCity },
  { key: 'state', label: 'State', valueGetter: (row) => row.puState }
];

const pickupSubLabels: HeaderSubLabel[] = pickupSubColumns.map((sub) => ({
  label: sub.label,
  field: subColumnField(pickupField, sub.key)
}));

const pickupDefinition: GridColDefExtended<DispatchLeg> = {
  field: pickupField,
  minWidth: 75,
  width: 120,
  headerName: 'PU Customer|||PU City|||PU State',
  headerDisplayName,
  subColumns: pickupSubColumns,
  /** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
   * static secondaryText instead of mounting the grid-subscribing sort labels per card */
  renderHeader: (params) => (
    <RenderHeader
      primaryText={headerDisplayName}
      subLabels={params.field ? pickupSubLabels : undefined}
      secondaryText='City, State'
    />
  ),
  renderCell: ({ row }) => (
    <LocationCell
      editable
      name={row.puName}
      city={row.puCity}
      state={row.puState}
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { puName, puCity, puState } = row;
        return caseInsensitiveIncludes(value, [puName, puCity, puState]);
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    `${row.puName?.replaceAll(',', '') ?? ''}|||${
      row.puCity?.replaceAll(',', '') ?? ''
    }|||${row.puState?.replaceAll(',', '') ?? ''}`.replaceAll(',', ''),
  sortable: true,
  sortComparator: (v1, v2, cellParams1) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      return defaultSortComparator(v1.split('|||')[0], v2.split('|||')[0], cellParams1);
    }

    return 0;
  },
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <LocationEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

export default pickupDefinition;

const canUpdatePickupLocation = (row: DispatchLeg) => {
  const legStatus = getEnumValueByKey(LegStatus, row.legStatus) as LegStatus;
  return ![LegStatus.PU, LegStatus.DL].includes(legStatus);
};

const LocationEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [patchLegs] = usePatchLegsMutation();
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));
  const [locValue, setLocValue] = useState<LegLocation | null>(() => {
    if (row.puId) {
      return {
        id: row.puId,
        name: row.puName
      };
    }
    return null;
  });

  /** The clicked leg gates the edit; a batch save extends it to the eligible selected legs */
  const canUpdate = canUpdatePickupLocation(row);

  const saveHandler = useCallback(async (scope: EditSaveScope) => {
    // Only need to call API if they changed the value
    if (locValue?.id !== row.puId) {
      const targetRows = resolveEditTargetRows(scope, row, selectedRows, canUpdatePickupLocation);

      await patchLegs({
        terminalNumber,
        legIds: targetRows.map((targetRow) => targetRow.legId).filter((legId) => legId > 0),
        leg: {
          puId: locValue?.id ?? null
        }
      })
        .unwrap()
        .then((response) => {
          response.errors.forEach((error) => {
            appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
          });
        })
        .catch((error: unknown) => {
          appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
        });
    }
  }, [appDispatch, terminalNumber, patchLegs, locValue, row, selectedRows]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <div style={{ padding: '12px 0' }}>
      <DisabledTooltip message={isLoading ? 'Updating...' : !canUpdate ? 'Pickup Location cannot be changed once the leg is picked up' : undefined}>
        <ButtonDropdown
          ButtonContent={LocationButtonContent}
          buttonContentProps={{
            location: locValue,
            emptyValueText: 'No Location Assigned'
          }}
          buttonConfig={{
            width: '300px'
          }}
          PopupContent={LocationListContent}
          fieldOnChange={(value: unknown) => {
            setLocValue(value as LegLocation);
          }}
          label={'Pickup Location'}
          onClear={() => {
            setLocValue(null);
          }}
          isDisabled={isLoading || !canUpdate}
        />
      </DisabledTooltip>
    </div>
  );
});
